import Token from './token';
import TokenV2 from './tokenv2';
import type { StreamModel } from './stream';

type TokenImpl = TokenV2 | Token;

/**
 * Caches one Token/TokenV2 instance per asset id.
 *
 * ConfigModel.setStream() used to create a fresh token instance on every
 * call, including once per playlist item while building the JW config. A
 * license request for the outgoing asset could still be in flight (or
 * retried, e.g. on key rotation) after setStream() moved on to the next
 * asset, so it ended up resolving a token minted for the wrong asset id -
 * DRMtoday then rejects it with AUTH_ASSET_NOT_IN_CRT (svp/issues#3753).
 * Keeping the token instance alive per asset id lets a request always
 * resolve the token for the asset it was made for.
 */
export class TokenStore {
    private current: { id: number; token: TokenImpl } | undefined;
    private previous: { id: number; token: TokenImpl } | undefined;

    get({ stream, useV2 }: { stream: StreamModel; useV2: boolean }): TokenImpl {
        const id = stream.getId();

        const getExisting = () => {
            if (this.current?.id === id) {
                return this.current.token;
            }

            if (this.previous?.id === id) {
                return this.previous.token;
            }

            return undefined;
        };

        const existing = getExisting();

        if (existing) {
            return existing;
        }

        const token = useV2 ? new TokenV2() : new Token();

        this.previous = this.current;
        this.current = { token, id };

        return token;
    }
}
